import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import UserShoppingCartViewProductsSection from "./sections/products";
import UserShoppingCartViewSummarySection from "./sections/summary";
import {routeFunctions} from "../../../../../core/models/static/routes/app-routes";
import Button from "../../../../components/base/button/button";
import Api from "../../../../../core/services/api/api";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {ReactComponent as EmptyCart} from '../../../../../assets/images/art-like/empty_cart.svg'
import useAccessLevels from "../../../../hooks/use-access-levels";
import {useConfigureNotebook} from "../../../../hooks/use-notebook-config";
import {Paper, Typography} from "@mui/material";
import CustomerMemberAutocomplete from "../../../../components/text-fields/autocomplete/customer-member-autocomplete";
import {Link, useParams} from "react-router-dom";
import {useAppSelector} from "../../../../redux";
import useQuery from "../../../../hooks/use-query";
import useBehavioralAnalyticsTool from "../../../../hooks/use-behavioural-analytics-tool";

const CustomerShoppingCartView = () => {
    const params = useParams();
    const [loading, setLoading] = useState(true);
    const [cart, setCart] = useState();
    const [query, setQuery] = useQuery();
    const [closingCart, setClosingCart] = useState(false)
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('customer-shopping-cart-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    const defaultCustomer = useAppSelector(state => state.customerInformation);

    const openQuoteLink = useMemo(() => routeFunctions.customer.sales.quotes.single(params.customerId, cart?.quoteId ?? 0), [cart?.quoteId, params.customerId])
    const selectedUser = useMemo(() => query?.user ? JSON.parse(query?.user) : null, [query?.user]);

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        if (!defaultCustomer?.code)
            return;
        configureNotebookTags((noteTags) => noteTags.customerSalesShoppingCart(defaultCustomer.code));
        configureBehavioralTool((tags) => tags.customerSalesShoppingCart(defaultCustomer.code));
    }, [configureBehavioralTool, configureNotebookTags, defaultCustomer]);

    /**
     * Fetches the shopping cart of the user from the server.
     * @type {function(): Promise<void>}
     */
    const getShoppingCart = useCallback(async () => {
        setLoading(true);
        const response = await Api.getCurrentShoppingCartForUser(selectedUser?.id);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag && response?.data) {
            response.data.parts = response.data.parts?.filter(e => e.partNo && e.partNo.length);
            if (!response.data.parts.length) {
                setCart(null);
            } else {
                setCart(response.data);
            }
        } else {
            setCart(null);
        }
    }, [isMounted, selectedUser?.id])

    /**
     * With each change tin the [userId] of the url params:
     * - fetches the shopping cart of the user from the server.
     */
    useEffect(() => {
        if (selectedUser?.id) {
            void getShoppingCart();
        }
    }, [getShoppingCart, selectedUser?.id])

    /**
     * Changes the selected user with the given user information.
     * @type {function(*=, *): void}
     */
    const onNewUserSelected = useCallback((_, user) => {
        setQuery(prevState => ({...prevState, user: user ? JSON.stringify(user) : undefined}))
        setCart(null);
    }, [setQuery])

    /**
     * Closes the cart for the user by calling the api from server and if the result is successful, then sets the
     * cart to null.
     */
    const closeCart = useCallback(async () => {
        setClosingCart(true);
        const response = await Api.closeShoppingCart(selectedUser?.id);
        if (!isMounted())
            return;
        setClosingCart(false);
        if (response?.resultFlag) {
            setCart(null);
        }
    }, [isMounted, selectedUser?.id])

    return (
        <>
            <div className={'d-flex align-items-center justify-content-between mb-4'}>
                {
                    !selectedUser?.id &&
                    <Typography variant={'h6'} fontWeight={600}>
                        {defaultCustomer?.name ?? ""}'s Members Shopping Cart
                    </Typography>
                }
                {
                    !!selectedUser?.id &&
                    <Typography variant={'h6'} fontWeight={600}>
                        {selectedUser?.firstName ?? ""} {selectedUser?.lastName ?? ""}'s Shopping Cart
                    </Typography>
                }
                {
                    (cart && !loading) &&
                    <>
                        <Button
                            variant={'outlined'}
                            color={'error'}
                            onClick={closeCart}
                            disabled={closingCart}
                        >
                            {
                                closingCart
                                    ? 'Closing Cart...'
                                    : 'Close Cart'
                            }
                        </Button>
                    </>
                }
            </div>
            <Paper className='d-flex w-100 p-3 flex-column mb-4'>
                <CustomerMemberAutocomplete
                    label={`Members of ${defaultCustomer?.name ?? "Customer"}`}
                    customerId={Number(params.customerId)}
                    value={selectedUser}
                    onChange={onNewUserSelected}
                    fullWidth
                />
            </Paper>
            {
                selectedUser?.id &&
                <>
                    {
                        (loading || cart) &&
                        <Row className='no-gutters align-items-start justify-content-center justify-content-md-between'>
                            <Col xs={12} lg={7} xl={8} className='mb-3 p-2 order-2 order-md-1'>
                                <UserShoppingCartViewProductsSection
                                    cart={cart}
                                    cartLoading={loading}
                                    setCart={setCart}
                                    userId={selectedUser?.id}
                                    customerId={Number(params.customerId)}
                                />
                            </Col>
                            <Col xs={12} lg={5} xl={4} className='mb-3 p-2 order-1 order-md-2'>
                                <UserShoppingCartViewSummarySection
                                    cart={cart}
                                    openQuoteLink={openQuoteLink}
                                    loading={loading}
                                />
                            </Col>
                        </Row>
                    }
                    {
                        !(loading || cart) &&
                        <Col className={'align-items-center justify-content-center d-flex flex-column'}>
                            <EmptyCart className={'svg'} style={{width: 'min(500px, 90vw)'}}/>
                            <p className='text-sm text-color-1 text-center'>
                                This user does not have an active shopping cart.
                            </p>
                            <div className='w-100 my-3'/>
                            {
                                operatorAccessLevels.packages.sales.quotesList &&
                                <Button
                                    variant={'outlined'}
                                    color={'primary'}
                                    component={Link}
                                    to={routeFunctions.customer.sales.quotes.base(params.customerId)}
                                >
                                    See Customer Quotes
                                </Button>
                            }

                        </Col>
                    }
                </>
            }
            {
                !selectedUser?.id &&
                <Paper className='paper w-100 px-3 py-5 flex-wrap justify-content-start align-items-start mt-4'>
                    <p className={'text-center text-sm w-100'}>
                        Please select a user to view their shopping cart.
                    </p>
                </Paper>
            }
        </>
    );
}


export default CustomerShoppingCartView;
